import i18n from "../i18n";
/**
 * useSpeechRecognition — V4.8 dual-engine speech-to-text.
 *
 * Engine 1 "webspeech": browser SpeechRecognition (Chrome desktop /
 * Android). Engine 2 "recorder": MediaRecorder → POST /api/ai/transcribe
 * (Whisper via backend) — the SAME shared STT backend the native app
 * uses. Recorder is the default on iOS (SpeechRecognition there is
 * unreliable: `service-not-allowed` unless Siri dictation is enabled)
 * and the automatic fallback when webspeech fails at runtime.
 *
 * Interface (unchanged for callers):
 *   isSupported · isListening · isProcessing · start() · stop() ·
 *   toggle() · errorMessage
 * Transcripts go to onTranscript(text) — NEVER auto-sent.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../api/client";

function getSpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function recorderSupported() {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== "undefined" &&
    !!window.MediaRecorder
  );
}

const LOCALES = { de: "de-DE", en: "en-US", hr: "hr-HR" };

function defaultLocale() {
  const key = (i18n.language || "de").split("-")[0];
  return LOCALES[key] || "de-DE";
}

// Webspeech errors that mean "engine broken here" → switch to recorder.
const FALLBACK_ERRORS = new Set([
  "service-not-allowed",
  "network",
  "audio-capture",
  "language-not-supported",
]);

const MAX_RECORD_MS = 60000;

export function useSpeechRecognition({ locale, onTranscript } = {}) {
  const Ctor = getSpeechRecognitionCtor();
  const canWebspeech = !!Ctor && !isIOSDevice();
  const canRecord = recorderSupported();
  const isSupported = canWebspeech || canRecord;

  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const engineRef = useRef(canWebspeech ? "webspeech" : "recorder");
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const capTimerRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  /* ---------------- recorder engine (Whisper backend) -------------- */
  const stopRecorder = useCallback(() => {
    clearTimeout(capTimerRef.current);
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try {
        mr.stop();
      } catch (_e) {
        /* noop */
      }
    }
    setIsListening(false);
  }, []);

  const startRecorder = useCallback(async () => {
    if (!canRecord) return;
    setErrorMessage(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : window.MediaRecorder.isTypeSupported("audio/mp4")
          ? "audio/mp4"
          : "";
      const mr = mime
        ? new window.MediaRecorder(stream, { mimeType: mime })
        : new window.MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, {
          type: mr.mimeType || "audio/webm",
        });
        chunksRef.current = [];
        if (blob.size < 800) return; // too short — nothing was said
        setIsProcessing(true);
        try {
          const ext = (mr.mimeType || "").includes("mp4") ? "mp4" : "webm";
          const fd = new FormData();
          fd.append("file", blob, `speech.${ext}`);
          fd.append("language", (i18n.language || "de").split("-")[0]);
          const { data } = await apiClient.post("/ai/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data?.text || "").trim();
          if (text && onTranscriptRef.current) onTranscriptRef.current(text);
        } catch (_e) {
          setErrorMessage(i18n.t("common:speech.unavailable"));
        } finally {
          setIsProcessing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsListening(true);
      capTimerRef.current = setTimeout(stopRecorder, MAX_RECORD_MS);
    } catch (_e) {
      setErrorMessage(i18n.t("common:speech.micDenied"));
      setIsListening(false);
    }
  }, [canRecord, stopRecorder]);

  /* ---------------- webspeech engine -------------------------------- */
  useEffect(() => {
    if (!canWebspeech) return undefined;
    const rec = new Ctor();
    rec.lang = locale || defaultLocale();
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      const last = event.results?.[event.results.length - 1];
      if (!last || !last.isFinal) return;
      const transcript = last[0]?.transcript || "";
      if (transcript && onTranscriptRef.current) {
        onTranscriptRef.current(transcript.trim());
      }
    };
    rec.onerror = (event) => {
      const code = event?.error || "unknown";
      setIsListening(false);
      if (FALLBACK_ERRORS.has(code) && canRecord) {
        // Engine broken on this browser — permanently switch to the
        // Whisper recorder and retry the capture seamlessly.
        engineRef.current = "recorder";
        startRecorder();
        return;
      }
      const msg =
        code === "not-allowed"
          ? i18n.t("common:speech.micDenied")
          : code === "no-speech"
            ? i18n.t("common:speech.noSpeech")
            : i18n.t("common:speech.unavailable");
      setErrorMessage(msg);
    };
    rec.onend = () => setIsListening(false);

    recognitionRef.current = rec;
    return () => {
      try {
        rec.abort();
      } catch (_e) {
        /* noop */
      }
      recognitionRef.current = null;
    };
  }, [Ctor, canWebspeech, canRecord, locale, startRecorder]);

  /* ---------------- public API -------------------------------------- */
  const start = useCallback(() => {
    if (!isSupported || isProcessing) return;
    setErrorMessage(null);
    if (engineRef.current === "webspeech" && recognitionRef.current) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (_e) {
        /* start() on an active instance throws — ignore */
      }
      return;
    }
    startRecorder();
  }, [isSupported, isProcessing, startRecorder]);

  const stop = useCallback(() => {
    if (engineRef.current === "webspeech" && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (_e) {
        /* noop */
      }
      setIsListening(false);
      return;
    }
    stopRecorder();
  }, [stopRecorder]);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  return { isSupported, isListening, isProcessing, start, stop, toggle, errorMessage };
}

export default useSpeechRecognition;
